import React, { useState, useEffect } from 'react';
import { landedCost as landedCostApi } from '../utils/api';
import { fmtBRL, fmtUSD, fmtRMB, totalDays, ORDER_STATUS_PT, STATUS_PT } from '../utils/helpers';

// Rótulo da fase para o cabeçalho do relatório impresso.
function phaseLabel(q) {
  if (!q.order_type || q.order_type === 'quotation') return 'COTAÇÃO';
  return q.order_type === 'sample' ? 'PEDIDO DE AMOSTRA' : 'PEDIDO (BULK)';
}

// Relatório imprimível — layout limpo e dedicado (some da tela, aparece só no
// print via CSS .print-report). Cobre qualquer fase: dados gerais, itens e,
// quando for pedido/amostra com custo efetivo, o quadro de Nacionalização.
export default function PrintReport({ quotation: q }) {
  const isRMB = q.currency === 'RMB';
  const isOrder = q.order_type && q.order_type !== 'quotation';
  const [nac, setNac] = useState(null);

  useEffect(() => {
    let alive = true;
    if (isOrder) landedCostApi.ofQuotation(q.id).then(d => { if (alive) setNac(d); }).catch(() => {});
    return () => { alive = false; };
  }, [q.id, isOrder]);

  const statusTxt = isOrder ? (ORDER_STATUS_PT[q.status] || q.status) : (STATUS_PT[q.status] || q.status);
  const printedAt = new Date().toLocaleString('pt-BR');
  const items = q.items || [];
  const t = nac?.totals;

  return (
    <div className="print-report">
      <div className="pr-head">
        <img src="/logo-charth.png" alt="Charth" className="pr-logo" />
        <div className="pr-head-right">
          <div className="pr-phase">{phaseLabel(q)}</div>
          <div className="pr-ref">{q.reference}</div>
          <div className="pr-printed">Impresso em {printedAt}</div>
        </div>
      </div>

      <table className="pr-info">
        <tbody>
          <tr><th>Fornecedor</th><td>{q.supplier_name || '—'}</td><th>Coleção</th><td>{q.collection || '—'}</td></tr>
          <tr><th>País</th><td>{q.supplier_country || q.country || '—'}</td><th>Status</th><td>{statusTxt}</td></tr>
          <tr>
            <th>Moeda</th><td>{isRMB ? '¥ RMB' : '$ USD'}</td>
            <th>Câmbio</th><td>R$ {Number(q.locked_exchange_rate || q.exchange_rate || 0).toFixed(4)}</td>
          </tr>
          <tr>
            <th>Multiplicador</th><td>×{Number(q.trading_cost_multiplier || q.other_costs_multiplier || 1.6).toFixed(2)}</td>
            <th>Agente</th><td>{q.agent_name || '—'}</td>
          </tr>
        </tbody>
      </table>
      {q.notes ? <div className="pr-notes">💬 {q.notes}</div> : null}

      <div className="pr-section">Itens ({items.length})</div>
      <table className="pr-table">
        <thead>
          <tr>
            <th>#</th><th>Produto</th><th>Especificação</th><th>NCM</th>
            <th className="pr-right">Qtd</th>
            <th className="pr-right">Preço {isRMB ? 'RMB' : 'USD'}</th>
            <th className="pr-right">Confirmado</th>
            <th className="pr-right">Lead</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const price = it.negotiated_price ?? it.target_price ?? 0;
            const conf = it.confirmed_price ?? it.negotiated_price;
            const lead = totalDays(it, q.shipping_days, q.customs_days);
            return (
              <tr key={it.id || i}>
                <td>{i + 1}</td>
                <td className="pr-strong">{it.product}</td>
                <td>{it.specification || '—'}</td>
                <td className="pr-mono">{it.ncm || '—'}</td>
                <td className="pr-right">{it.quantity || '—'}</td>
                <td className="pr-right pr-mono">{price ? (isRMB ? fmtRMB(price) : fmtUSD(price)) : '—'}</td>
                <td className="pr-right pr-mono">{conf ? (isRMB ? fmtRMB(conf) : fmtUSD(conf)) : '—'}</td>
                <td className="pr-right">{lead > 0 ? `${lead}d` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isOrder && nac && Array.isArray(nac.items) && nac.items.length > 0 && t && (
        <>
          <div className="pr-section">Nacionalização / Custo Efetivo</div>
          <div className="pr-kpis">
            <div><span>Desembolso</span><strong>{fmtBRL(t.desembolso)}</strong></div>
            <div><span>Recuperável</span><strong>{fmtBRL(t.recuperavel)}</strong></div>
            <div className="pr-kpi-accent"><span>Custo Efetivo</span><strong>{fmtBRL(t.custoEfetivo)}</strong></div>
          </div>
          <table className="pr-table">
            <thead>
              <tr>
                <th>Produto</th><th>NCM</th>
                <th className="pr-right">Val. Aduaneiro</th><th className="pr-right">II</th><th className="pr-right">IPI</th>
                <th className="pr-right">PIS</th><th className="pr-right">COFINS</th><th className="pr-right">ICMS</th>
                <th className="pr-right">Custo Unit. Efetivo</th>
              </tr>
            </thead>
            <tbody>
              {nac.items.map((it, i) => (
                <tr key={i}>
                  <td className="pr-strong">{it.product || `Item ${i + 1}`}</td>
                  <td className="pr-mono">{it.ncm || '—'}</td>
                  <td className="pr-right">{fmtBRL(it.VA)}</td>
                  <td className="pr-right">{fmtBRL(it.ii)}</td>
                  <td className="pr-right">{fmtBRL(it.ipi)}</td>
                  <td className="pr-right">{fmtBRL(it.pis)}</td>
                  <td className="pr-right">{fmtBRL((it.cofins || 0) + (it.cofinsAd || 0))}</td>
                  <td className="pr-right">{fmtBRL(it.icms)}</td>
                  <td className="pr-right pr-strong">{it.qty > 0 ? fmtBRL(it.custoUnit) : '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Total</td>
                <td className="pr-right">{fmtBRL(t.VA)}</td>
                <td className="pr-right">{fmtBRL(t.ii)}</td>
                <td className="pr-right">{fmtBRL(t.ipi)}</td>
                <td className="pr-right">{fmtBRL(t.pis)}</td>
                <td className="pr-right">{fmtBRL((t.cofins || 0) + (t.cofinsAd || 0))}</td>
                <td className="pr-right">{fmtBRL(t.icms)}</td>
                <td className="pr-right">{fmtBRL(t.custoEfetivo)}</td>
              </tr>
            </tfoot>
          </table>
          <div className="pr-caption">Lucro Real: IPI/PIS/COFINS/ICMS são crédito recuperável; o custo efetivo já desconta os créditos.</div>
        </>
      )}

      <div className="pr-foot">Charth Sourcing System · {q.reference}</div>
    </div>
  );
}
